// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  active: "active",
  visible: "visible",
  current: "current",
};

const SELECTORS = {
  navLevel1FirstLink: "ul.nav-level1 > li:first-child > a",
  navItemParentDirectChildren: ":scope > li.nav-item-parent",
  navItemParentDirectAnchor: ":scope > a",
  navItemParentDirectSubUL: ":scope > ul",
  navItemParentDeepSubUL: ":scope > ul > li.nav-item-parent > ul",
  allNavItemParentAnchors: "ul li.nav-item-parent > a",
  allNavItemParentBackAnchors: "ul li.nav-item-parent-back > a",
  currentUL: "ul.current",
  compFlyout: ".comp-flyout",
  navigationMobile: ".navigation-mobile",
  homeLink: "ul > li.nav-item-home > a",
  rootULs: ":scope > ul > li > ul",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class ShellNavigation {
  private el: HTMLElement;
  private abortController: AbortController;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
  }

  init(): void {
    this.setActiveNav();
    this.insertParentLinks();
    this.insertBackLinks();
    this.insertHomeBackLinks();
    this.bindEvents();
    this.calculateContainer();
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Active nav setup ────────────────────────────────────────────────────────

  private setActiveNav(): void {
    const activePath = this.el.dataset.currentActive ?? "";

    let currentItem: HTMLAnchorElement | null =
      Array.from(this.el.querySelectorAll<HTMLAnchorElement>("a")).find(
        (a) => a.getAttribute("href") === activePath,
      ) ?? null;

    if (!currentItem) {
      // active link not in tree — fall back to first root item
      currentItem = this.el.querySelector<HTMLAnchorElement>(
        SELECTORS.navLevel1FirstLink,
      );
      currentItem?.closest("ul")?.classList.add(CLASSES.current);
    } else {
      currentItem.parentElement?.classList.add(CLASSES.active);

      // find sibling <ul> (the submenu) within the same <li>
      const siblingUL = currentItem.parentElement
        ? (Array.from(currentItem.parentElement.children).find(
            (child) =>
              child !== currentItem && child.tagName.toLowerCase() === "ul",
          ) as HTMLElement | null)
        : null;

      if (siblingUL) {
        siblingUL.classList.add(CLASSES.visible, CLASSES.current);
      } else {
        currentItem.closest("ul")?.classList.add(CLASSES.current);
      }
    }

    // mark all <ul> ancestors visible
    if (currentItem) {
      let ancestor: Element | null = currentItem.parentElement;
      while (ancestor && ancestor !== this.el) {
        if (ancestor.tagName.toLowerCase() === "ul") {
          (ancestor as HTMLElement).classList.add(CLASSES.visible);
        }
        ancestor = ancestor.parentElement;
      }
    }
  }

  // ─── DOM setup: parent-page links ────────────────────────────────────────────

  private insertParentLinks(): void {
    const activePath = this.el.dataset.currentActive ?? "";

    this.el.querySelectorAll<HTMLElement>("ul").forEach((ul) => {
      ul.querySelectorAll<HTMLElement>(
        SELECTORS.navItemParentDirectChildren,
      ).forEach((parentItem) => {
        const anchor = parentItem.querySelector<HTMLAnchorElement>(
          SELECTORS.navItemParentDirectAnchor,
        );
        const subUL = parentItem.querySelector<HTMLElement>(
          SELECTORS.navItemParentDirectSubUL,
        );
        if (!anchor || !subUL) return;

        const li = document.createElement("li");
        li.className = "nav-item nav-item-parent-link";
        if (anchor.getAttribute("href") === activePath) {
          li.classList.add(CLASSES.active);
        }
        li.append(anchor.cloneNode(true));
        subUL.prepend(li);
      });
    });
  }

  // ─── DOM setup: back links ───────────────────────────────────────────────────

  private insertBackLinks(): void {
    this.el.querySelectorAll<HTMLElement>("ul").forEach((ul) => {
      ul.querySelectorAll<HTMLElement>(
        SELECTORS.navItemParentDirectChildren,
      ).forEach((parentItem) => {
        const anchor = parentItem.querySelector<HTMLAnchorElement>(
          SELECTORS.navItemParentDirectAnchor,
        );
        if (!anchor) return;

        parentItem
          .querySelectorAll<HTMLElement>(SELECTORS.navItemParentDeepSubUL)
          .forEach((subUL) => {
            const li = document.createElement("li");
            li.className = "nav-item nav-item-parent-back";
            li.append(anchor.cloneNode(true));
            subUL.prepend(li);
          });
      });
    });
  }

  // ─── DOM setup: home back links ──────────────────────────────────────────────

  private insertHomeBackLinks(): void {
    const homeLink = this.el.querySelector<HTMLAnchorElement>(
      SELECTORS.homeLink,
    );
    if (!homeLink) return;

    this.el.querySelectorAll<HTMLElement>(SELECTORS.rootULs).forEach((rootUL) => {
      const li = document.createElement("li");
      li.className = "nav-item nav-item-parent-back";
      li.append(homeLink.cloneNode(true));
      rootUL.prepend(li);
    });
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;

    this.el
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.allNavItemParentAnchors)
      .forEach((link) => {
        link.addEventListener(
          "click",
          (e) => this.showSubnavigation(e, link),
          { signal },
        );
      });

    this.el
      .querySelectorAll<HTMLAnchorElement>(SELECTORS.allNavItemParentBackAnchors)
      .forEach((link) => {
        link.addEventListener("click", (e) => this.clickParent(e, link), {
          signal,
        });
      });

    window.addEventListener("resize", () => this.calculateContainer(), {
      signal,
    });
  }

  // ─── Container height ────────────────────────────────────────────────────────

  private calculateContainer(): void {
    this.calculateNavHeight(
      this.el.querySelector<HTMLElement>(SELECTORS.currentUL),
    );
  }

  // ─── Show subnavigation ──────────────────────────────────────────────────────

  private showSubnavigation(
    event: MouseEvent,
    link: HTMLAnchorElement,
  ): void {
    const subUL = link.nextElementSibling as HTMLElement | null;
    if (!subUL || subUL.tagName.toLowerCase() !== "ul") return;

    event.preventDefault();

    const expand = (): void => {
      this.el
        .querySelectorAll<HTMLElement>(SELECTORS.currentUL)
        .forEach((ul) => ul.classList.remove(CLASSES.current));
      subUL.classList.add(CLASSES.visible, CLASSES.current);
      this.calculateNavHeight(subUL);
    };

    const flyout = document.querySelector<HTMLElement>(SELECTORS.compFlyout);
    if (flyout) {
      flyout.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(expand, 200);
    } else {
      expand();
    }
  }

  // ─── Click parent (back navigation) ─────────────────────────────────────────

  private clickParent(event: MouseEvent, link: HTMLAnchorElement): void {
    event.preventDefault();

    const subUL = link.closest("ul") as HTMLElement | null;
    if (!subUL) return;

    subUL.classList.remove(CLASSES.visible, CLASSES.current);

    const parentLi = subUL.parentElement;
    if (!parentLi || parentLi.tagName.toLowerCase() !== "li") return;

    const parentUL = parentLi.parentElement as HTMLElement | null;
    if (!parentUL || parentUL.tagName.toLowerCase() !== "ul") return;

    parentUL.classList.add(CLASSES.current);
    this.calculateNavHeight(parentUL);
  }

  // ─── Nav height calculation ──────────────────────────────────────────────────

  private calculateNavHeight(current: HTMLElement | null): void {
    if (!current) return;

    const navMobile = document.querySelector<HTMLElement>(
      SELECTORS.navigationMobile,
    );
    let tempShow = false;

    if (navMobile && getComputedStyle(navMobile).display === "none") {
      tempShow = true;
      navMobile.style.display = "block";
    }

    this.el.style.height = current.offsetHeight + "px";

    if (tempShow && navMobile) {
      navMobile.style.display = "";
    }

    setTimeout(() => {
      this.el.dispatchEvent(
        new CustomEvent("navigationChanged", { bubbles: true }),
      );
    }, 500);
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="shell-navigation"]')
      .forEach((el) => {
        new ShellNavigation(el).init();
      });
  }
}
